import { useCallback, useEffect, useRef, useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Provider as ReduxProvider } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'

import { AbilityProvider } from '@shared-ui/components/AbilityContext'
import { SnackBarProvider } from '@shared-ui/components/SnackBar'

import { ErrorBoundary } from 'shared/ui/ErrorBoundary'

import {
    ConfirmationModalProvider,
    FilterProvider,
    ModalProvider,
    NotificationProvider,
} from './providers'
import { ThemeProvider } from './providers/theme'
import { refreshUserRoles } from 'shared/auth'
import { roleAbilities } from './roleSchema'
import { AppRoutes } from './routes'
import { store } from './store'

/** Interval in ms for periodic token refresh to pick up role changes (5 minutes) */
const ROLE_REFRESH_INTERVAL = 5 * 60 * 1000

/** Custom event name dispatched by LoginForm after successful login */
const USER_DATA_UPDATED_EVENT = 'user-data-updated'

/** Default role when no roles are assigned */
const DEFAULT_ROLE = 'user'

interface RoleAssignment {
    id: number
    createdAt: string
    updatedAt: string
    roleId: number
    userId: string
    assignedById: string | null
    organizationId: string | null
    sports: unknown[]
    units: unknown[]
    assignedBy: {
        id: string
        unisportId: number
        firstName: string
        lastName: string
        imageKey: string
        showProfilePhoto: boolean
    } | null
    role: {
        id: number
        key: string
        name: string
        createdAt: string
        updatedAt: string
        permissions: unknown[]
        canAssignRoles: unknown[]
    }
}

interface StoredUser {
    id?: string
    userRoles?: RoleAssignment[]
    isVerified?: boolean
}

function readUserFromStorage(): StoredUser | null {
    const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user')
    if (!raw) return null
    try {
        return JSON.parse(raw) as StoredUser
    } catch {
        return null
    }
}

function extractRoleData(userData: StoredUser) {
    const userRoles = userData.userRoles ?? []

    const roleKeys = userRoles
        .map((userRole) => userRole.role?.key)
        .filter(Boolean) as string[]

    const organizationIds = userRoles
        .map((role) => role.organizationId)
        .filter((id): id is string => id != null)

    return {
        roles: userRoles,
        roleKeys: roleKeys.length > 0 ? roleKeys : [DEFAULT_ROLE],
        userId: userData.id ?? null,
        organizationIds,
    }
}

export const AppProviders = () => {
    const [userRoles, setUserRoles] = useState<string[]>([DEFAULT_ROLE])
    const [roles, setRoles] = useState<RoleAssignment[]>([])
    const [userId, setUserId] = useState<string | null>(null)
    const [organizationIds, setOrganizationIds] = useState<string[]>([])
    const [abilityKey, setAbilityKey] = useState(0)
    const isInitialized = useRef(false)

    const loadUserRoles = useCallback(() => {
        const userData = readUserFromStorage()
        if (!userData) {
            setUserRoles([DEFAULT_ROLE])
            setRoles([])
            setUserId(null)
            setOrganizationIds([])
            return
        }

        const data = extractRoleData(userData)
        setRoles(data.roles)
        setUserRoles(data.roleKeys)
        setUserId(data.userId)
        setOrganizationIds(data.organizationIds)
        setAbilityKey((prev) => prev + 1)
    }, [])

    // Load initial user data on mount
    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true
            loadUserRoles()
        }
    }, [loadUserRoles])

    // Listen for the custom event dispatched after login
    useEffect(() => {
        const handleUserDataUpdated = () => {
            loadUserRoles()
        }

        window.addEventListener(USER_DATA_UPDATED_EVENT, handleUserDataUpdated)

        return () => {
            window.removeEventListener(
                USER_DATA_UPDATED_EVENT,
                handleUserDataUpdated
            )
        }
    }, [loadUserRoles])

    // Periodically refresh the token to pick up role changes made by admins.
    // The refresh decodes the new JWT and updates stored userRoles, then
    // fires 'user-data-updated' which triggers loadUserRoles above.
    useEffect(() => {
        const interval = setInterval(() => {
            const hasToken =
                sessionStorage.getItem('accessToken') ||
                localStorage.getItem('accessToken')
            if (hasToken) {
                refreshUserRoles(store.dispatch)
            }
        }, ROLE_REFRESH_INTERVAL)

        return () => clearInterval(interval)
    }, [])

    return (
        <ErrorBoundary>
            <HelmetProvider>
                <ReduxProvider store={store}>
                    <ThemeProvider>
                        <NotificationProvider>
                            <SnackBarProvider>
                                <Router>
                                    <ConfirmationModalProvider>
                                        <ModalProvider>
                                            <FilterProvider>
                                                <AbilityProvider
                                                    key={`ability-provider-${abilityKey}`}
                                                    role={userRoles}
                                                    userRoles={roles}
                                                    roleSchema={roleAbilities}
                                                    userId={userId}
                                                    organizationIds={
                                                        organizationIds
                                                    }
                                                >
                                                    <AppRoutes />
                                                </AbilityProvider>
                                            </FilterProvider>
                                        </ModalProvider>
                                    </ConfirmationModalProvider>
                                </Router>
                            </SnackBarProvider>
                        </NotificationProvider>
                    </ThemeProvider>
                </ReduxProvider>
            </HelmetProvider>
        </ErrorBoundary>
    )
}
